import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import UIPlaceholder from 'ui-library/UIPlaceholder';

import {
  GetBenchlingAppFeatureByAppAndFeatureId,
  GetBenchlingAppFeatureByAppAndFeatureIdVariables,
} from './__generated/GetBenchlingAppFeatureByAppAndFeatureId';
import {RemovedFeaturePlaceholder} from './DefaultPlaceholder';
import GenericCanvasShell from './GenericCanvasShell';

export const BENCHLING_APP_FEATURE_BY_ID_QUERY = gql`
  query GetBenchlingAppFeatureByAppAndFeatureId(
    $appInstallationId: StrictString!
    $featureDeveloperId: StrictString!
  ) @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingAppFeatureByAppAndFeatureId(
      appInstallationId: $appInstallationId
      featureDeveloperId: $featureDeveloperId
    ) {
      ...BenchlingAppFeatureGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingAppFeature').fragment}
`;

export interface CanvasCreateProps {
  featureUserDefinedId: string;
  appInstallationId: string;
  resourceId: string;
  canCreateCanvas: boolean;
  doCreateCanvas: (
    featureUserDefinedId: string,
    appInstallationId: string,
    resourceId: string
  ) => Promise<void>;
}

export default function CanvasCreate(props: CanvasCreateProps): JSX.Element {
  const [createInitiated, setCreateInitiated] = useState(false);

  const handleCreateCanvas = async (): Promise<void> => {
    if (props.doCreateCanvas && !createInitiated) {
      setCreateInitiated(true);
      try {
        await props.doCreateCanvas(props.featureUserDefinedId, props.appInstallationId, props.resourceId);
      } finally {
        setCreateInitiated(false);
      }
    }
  };
  return (
    <BenchlingQuery<GetBenchlingAppFeatureByAppAndFeatureId, GetBenchlingAppFeatureByAppAndFeatureIdVariables>
      query={BENCHLING_APP_FEATURE_BY_ID_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        appInstallationId: props.appInstallationId,
        featureDeveloperId: props.featureUserDefinedId,
      }}
    >
      {(data) => {
        if (isInaccessible(data.benchlingAppFeatureByAppAndFeatureId)) {
          return (
            <GenericCanvasShell
              appName=""
              featureName="App Feature Not Found"
              canvasSession={null}
              isFeatureMissing={true}
            >
              <RemovedFeaturePlaceholder />
            </GenericCanvasShell>
          );
        } else {
          return (
            <GenericCanvasShell
              appName={data.benchlingAppFeatureByAppAndFeatureId.benchlingApp.name}
              featureName={data.benchlingAppFeatureByAppAndFeatureId.name}
              dataTestAttrs={{component: 'GenericCanvasShell', key: 'canvasCreate', element: 'root'}}
            >
              {props.canCreateCanvas ? (
                <div className={css(styles.createContainer)}>
                  <UIPlaceholder
                    infoSection="Create to interact with app."
                    actionContent={
                      <Button
                        buttonStyle={ButtonStyle.PRIMARY}
                        text="Create"
                        size={ButtonSize.MEDIUM}
                        onClick={handleCreateCanvas}
                        disabled={createInitiated}
                        dataTestAttrs={{component: 'GenericCanvasShell', element: 'createCanvasButton'}}
                      />
                    }
                  />
                </div>
              ) : null}
            </GenericCanvasShell>
          );
        }
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  createContainer: {
    backgroundColor: GRAY_LIGHTER,
    padding: '20px',
  },
});
